/**
 * IconPicker 图标选择器组件
 *
 * @description 支持从 Lobe Icons 选择 AI 模型/供应商图标，或自己上传图片
 */
import { DeleteOutlined, LoadingOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Empty, Input, message, Popover, Segmented, Tabs, Tooltip, Upload } from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload';
import { createStyles } from 'antd-style';
import React, { useMemo, useState } from 'react';

import { uploadFileSignatureApiAdminUploadSignaturePost } from '@/services/admin/fileStorage';
import {
  calculateFileHash,
  DEFAULT_MAX_SIZE,
  formatFileSize,
  getFileMimeType,
  IMAGE_ACCEPT,
  isImageFile,
} from '../ImageUpload/utils';
import { LOBE_ICONS } from './icons';

const { Search } = Input;

// ==================== 图标分类配置 ====================

// 主流 AI 供应商
const PROVIDER_ICONS = [
  'OpenAI',
  'Anthropic',
  'Google',
  'Azure',
  'AzureAI',
  'Aws',
  'Bedrock',
  'Meta',
  'MetaAI',
  'Microsoft',
  'Nvidia',
  'IBM',
  'Apple',
  'Alibaba',
  'AlibabaCloud',
  'Baidu',
  'BaiduCloud',
  'Tencent',
  'TencentCloud',
  'Huawei',
  'HuaweiCloud',
  'ByteDance',
  'Volcengine',
];

// AI 模型图标
const MODEL_ICONS = [
  'Claude',
  'Gemini',
  'Gemma',
  'Grok',
  'Mistral',
  'Qwen',
  'DeepSeek',
  'ChatGLM',
  'Zhipu',
  'Baichuan',
  'Yi',
  'Moonshot',
  'Kimi',
  'Minimax',
  'Stepfun',
  'Doubao',
  'Spark',
  'Hunyuan',
  'Wenxin',
  'Rwkv',
  'PaLM',
  'Dalle',
  'Sora',
  'Flux',
  'Kling',
  'Hailuo',
  'Jimeng',
  'Vidu',
  'Nova',
  'CommandA',
  'Dbrx',
  'InternLM',
  'Skywork',
  'Tiangong',
  'Yuanbao',
  'Xuanyuan',
  'LLaVA',
  'Dolphin',
  'Aya',
  'CogVideo',
  'CogView',
  'Kolors',
  'GLMV',
];

// API 平台/中转服务
const PLATFORM_ICONS = [
  'Groq',
  'Together',
  'Fireworks',
  'Replicate',
  'HuggingFace',
  'Ollama',
  'OpenRouter',
  'Perplexity',
  'LmStudio',
  'Cohere',
  'Ai21',
  'DeepInfra',
  'Anyscale',
  'Lambda',
  'Baseten',
  'SambaNova',
  'Cerebras',
  'Hyperbolic',
  'Nebius',
  'Novita',
  'SiliconCloud',
  'ModelScope',
  'Xinference',
  'Vllm',
  'NewAPI',
  'AiHubMix',
  'ZeroOne',
  'Bailian',
  'Qiniu',
  'GiteeAI',
  'Inference',
  'Featherless',
  'Friendli',
  'Parasail',
  'LeptonAI',
  'CentML',
  'Crusoe',
  'Menlo',
  'Kluster',
  'Upstage',
  'Arcee',
  'Targon',
  'Straico',
  'ZenMux',
  'Glama',
  'Ai302',
  'Ai360',
  'PPIO',
  'SubModel',
  'CometAPI',
  'StateCloud',
  'BurnCloud',
  'StreamLake',
  'NPLCloud',
  'SophNet',
  'Infinigence',
  'WorkersAI',
  'VertexAI',
  'GoogleCloud',
  'Cloudflare',
];

// 应用/工具图标
const APP_ICONS = [
  'Cursor',
  'Copilot',
  'GithubCopilot',
  'Github',
  'Notion',
  'Figma',
  'Vercel',
  'Replit',
  'V0',
  'Coze',
  'Dify',
  'FastGPT',
  'LobeHub',
  'Monica',
  'Poe',
  'OpenChat',
  'OpenWebUI',
  'ComfyUI',
  'Gradio',
  'Colab',
  'LangChain',
  'LangGraph',
  'LangSmith',
  'Langfuse',
  'LlamaIndex',
  'CrewAI',
  'Phidata',
  'PydanticAI',
  'MetaGPT',
  'Mastra',
  'N8n',
  'Make',
  'Zapier',
  'MCP',
  'McpSo',
  'Smithery',
  'Cline',
  'Windsurf',
  'Trae',
  'Lovable',
  'Goose',
  'Greptile',
  'Phind',
  'Tavily',
  'Exa',
  'Search1API',
  'SearchApi',
  'Jina',
  'DocSearch',
  'Doc2X',
  'RSSHub',
  'Railway',
  'Zeabur',
  'NotebookLM',
  'AiStudio',
  'DeepL',
  'Unstructured',
];

// 图像/视频/音频生成
const MEDIA_ICONS = [
  'Midjourney',
  'Stability',
  'AdobeFirefly',
  'Adobe',
  'Ideogram',
  'Recraft',
  'Fal',
  'Bfl',
  'Glif',
  'Krea',
  'Civitai',
  'NovelAI',
  'Pollinations',
  'VectorizerAI',
  'TopazLabs',
  'Clipdrop',
  'Automatic',
  'Runway',
  'Pika',
  'Luma',
  'DreamMachine',
  'Haiper',
  'PixVerse',
  'Viggle',
  'Hedra',
  'Lightricks',
  'Tripo',
  'Suno',
  'Udio',
  'ElevenLabs',
  'ElevenX',
  'FishAudio',
  'Coqui',
  'AssemblyAI',
  'LiveKit',
  'CyberCut',
  'CapCut',
  'Player2',
  'Sync',
];

// 其他/研究机构
const OTHER_ICONS = [
  'DeepMind',
  'DeepAI',
  'DeepCogito',
  'AlephAlpha',
  'NousResearch',
  'BAAI',
  'TII',
  'EssentialAI',
  'Inception',
  'Inflection',
  'Liquid',
  'Morph',
  'Magic',
  'Manus',
  'MyShell',
  'Flowith',
  'Flora',
  'LongCat',
  'YouMind',
  'Qingyan',
  'SenseNova',
  'IFlyTekCloud',
  'AionLabs',
  'AiMass',
  'Agui',
  'Ai2',
  'AkashChat',
  'Bilibili',
  'BilibiliIndex',
  'Bing',
  'CodeFlicker',
  'CodeGeeX',
  'CopilotKit',
  'Higress',
  'Infermatic',
  'KwaiKAT',
  'Kwaipilot',
  'LG',
  'Relace',
  'Snowflake',
  'TuriX',
  'Voyage',
  'XAI',
  'XiaomiMiMo',
  'Yandex',
  'ZAI',
];

// 图标分类
const ICON_CATEGORIES: Record<string, { label: string; icons: string[] }> = {
  provider: { label: '供应商', icons: PROVIDER_ICONS },
  model: { label: '模型', icons: MODEL_ICONS },
  platform: { label: 'API平台', icons: PLATFORM_ICONS },
  app: { label: '应用工具', icons: APP_ICONS },
  media: { label: '媒体生成', icons: MEDIA_ICONS },
  other: { label: '其他', icons: OTHER_ICONS },
};

// 所有图标列表
const ALL_ICONS = Object.values(ICON_CATEGORIES).flatMap((cat) => cat.icons);

// ==================== 样式定义 ====================

const useStyles = createStyles(({ token, css }) => ({
  trigger: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    cursor: pointer;
    background-color: ${token.colorFillAlter};
    border: 1px dashed ${token.colorBorder};
    border-radius: ${token.borderRadius}px;
    transition: all ${token.motionDurationMid};

    &:hover {
      border-color: ${token.colorPrimary};
      background-color: ${token.colorFillSecondary};
    }
  `,
  triggerFocused: css`
    border-color: ${token.colorPrimary};
    box-shadow: 0 0 0 2px ${token.colorPrimaryBg};
  `,
  triggerContent: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: ${token.colorTextSecondary};
    font-size: 12px;
  `,
  iconPreview: css`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: ${token.borderRadius}px;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  `,
  iconOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity ${token.motionDurationMid};

    &:hover {
      opacity: 1;
    }
  `,
  deleteButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    font-size: 14px;
    color: #fff;
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transition: all ${token.motionDurationMid};

    &:hover {
      background-color: ${token.colorError};
    }
  `,
  popoverContent: css`
    width: 420px;
  `,
  segmented: css`
    margin-bottom: ${token.marginSM}px;
    width: 100%;
  `,
  search: css`
    margin-bottom: ${token.marginSM}px;
  `,
  tabContent: css`
    max-height: 280px;
    overflow: auto;
  `,
  iconGrid: css`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: ${token.marginXS}px;
    padding: 4px;
  `,
  iconItem: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    cursor: pointer;
    border-radius: ${token.borderRadius}px;
    border: 1px solid transparent;
    transition: all 0.2s;

    &:hover {
      background-color: ${token.colorPrimaryBg};
      border-color: ${token.colorPrimary};
    }
  `,
  iconItemSelected: css`
    background-color: ${token.colorPrimaryBg};
    border-color: ${token.colorPrimary};
  `,
  uploadArea: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${token.paddingLG}px;
    border: 1px dashed ${token.colorBorder};
    border-radius: ${token.borderRadius}px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: ${token.colorPrimary};
      background-color: ${token.colorFillAlter};
    }
  `,
  uploadIcon: css`
    font-size: 32px;
    color: ${token.colorTextSecondary};
    margin-bottom: ${token.marginXS}px;
  `,
  uploadText: css`
    color: ${token.colorTextSecondary};
    font-size: ${token.fontSizeSM}px;
  `,
  uploadHint: css`
    color: ${token.colorTextTertiary};
    font-size: ${token.fontSizeSM}px;
    margin-top: ${token.marginXXS}px;
  `,
  empty: css`
    padding: ${token.paddingLG}px;
  `,
}));

// ==================== 组件定义 ====================

export interface IconPickerProps {
  /** 图标值（URL 或 Lobe Icon 名称） */
  value?: string;
  /** 值变化回调 */
  onChange?: (value: string | undefined) => void;
  /** 占位文字 */
  placeholder?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 图标宽度 */
  width?: number;
  /** 图标高度 */
  height?: number;
}

const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onChange,
  placeholder = '选择图标',
  disabled = false,
  width = 80,
  height = 80,
}) => {
  const { styles, cx } = useStyles();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'select' | 'upload'>('select');
  const [searchValue, setSearchValue] = useState('');
  const [uploading, setUploading] = useState(false);

  // 判断值是否为 URL
  const isUrl = value?.startsWith('http') || value?.startsWith('/');

  // 获取 Lobe Icon 组件
  const getLobeIconComponent = (
    iconName: string
  ): React.ComponentType<{ size?: number }> | null => {
    return LOBE_ICONS[iconName] || null;
  };

  // 判断值是否为 Lobe Icon
  const isLobeIcon = value && !isUrl && getLobeIconComponent(value);

  // 过滤图标
  const filteredIcons = useMemo(() => {
    if (!searchValue) return null;
    const lowerSearch = searchValue.toLowerCase();
    return ALL_ICONS.filter((name) => name.toLowerCase().includes(lowerSearch));
  }, [searchValue]);

  // 处理选择 Lobe Icon
  const handleSelectIcon = (iconName: string) => {
    onChange?.(iconName);
    setOpen(false);
    setSearchValue('');
  };

  // 处理删除
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(undefined);
  };

  // 上传前校验
  const beforeUpload = (file: RcFile): boolean => {
    if (!isImageFile(file)) {
      message.error('请上传图片文件');
      return false;
    }
    if (file.size > DEFAULT_MAX_SIZE) {
      message.error(`图片大小不能超过 ${formatFileSize(DEFAULT_MAX_SIZE)}`);
      return false;
    }
    return true;
  };

  // 自定义上传
  const customUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const uploadFile = file as RcFile;

    setUploading(true);

    try {
      const fileHash = await calculateFileHash(uploadFile);
      const response = await uploadFileSignatureApiAdminUploadSignaturePost({
        file_hash: fileHash,
        filename: uploadFile.name,
        file_size: uploadFile.size,
        type: 'image',
        mime_type: getFileMimeType(uploadFile),
      });

      if (response.code !== 0 || !response.data) {
        throw new Error(response.message || '获取上传签名失败');
      }

      const { is_exists, file_url, upload_url } = response.data;

      if (is_exists && file_url) {
        onChange?.(file_url);
        onSuccess?.(response.data);
        message.success('图片上传成功（秒传）');
        setOpen(false);
        return;
      }

      if (!upload_url) {
        throw new Error('获取上传地址失败');
      }

      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: uploadFile,
        headers: { 'Content-Type': getFileMimeType(uploadFile) },
      });

      if (!uploadResponse.ok) {
        throw new Error('文件上传失败');
      }

      const fileAccessUrl = upload_url.split('?')[0];
      onChange?.(fileAccessUrl);
      onSuccess?.(response.data);
      message.success('图片上传成功');
      setOpen(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '上传失败';
      message.error(errorMessage);
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  // 渲染 Lobe Icon
  const renderLobeIcon = (iconName: string, size: number = 32) => {
    const IconComponent = getLobeIconComponent(iconName);
    if (IconComponent) {
      return <IconComponent size={size} />;
    }
    return <span>{iconName}</span>;
  };

  // 渲染图标网格
  const renderIconGrid = (icons: string[]) => {
    if (icons.length === 0) {
      return (
        <div className={styles.empty}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未找到图标" />
        </div>
      );
    }
    return (
      <div className={styles.iconGrid}>
        {icons.map((iconName) => (
          <Tooltip key={iconName} title={iconName}>
            <div
              className={cx(styles.iconItem, value === iconName && styles.iconItemSelected)}
              onClick={() => handleSelectIcon(iconName)}
            >
              {renderLobeIcon(iconName, 28)}
            </div>
          </Tooltip>
        ))}
      </div>
    );
  };

  // Tab 项
  const tabItems = Object.entries(ICON_CATEGORIES).map(([key, { label, icons }]) => ({
    key,
    label: `${label} (${icons.length})`,
    children: <div className={styles.tabContent}>{renderIconGrid(icons)}</div>,
  }));

  // 渲染选择内容
  const renderSelectContent = () => (
    <>
      <Search
        className={styles.search}
        placeholder="搜索图标..."
        allowClear
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />
      {filteredIcons ? (
        <div className={styles.tabContent}>{renderIconGrid(filteredIcons)}</div>
      ) : (
        <Tabs items={tabItems} size="small" />
      )}
    </>
  );

  // 渲染上传内容
  const renderUploadContent = () => (
    <Upload
      accept={IMAGE_ACCEPT}
      showUploadList={false}
      beforeUpload={beforeUpload}
      customRequest={customUpload}
      disabled={uploading}
    >
      <div className={styles.uploadArea}>
        {uploading ? (
          <LoadingOutlined className={styles.uploadIcon} />
        ) : (
          <UploadOutlined className={styles.uploadIcon} />
        )}
        <span className={styles.uploadText}>{uploading ? '上传中...' : '点击上传图片'}</span>
        <span className={styles.uploadHint}>支持 JPG、PNG、GIF、WebP，最大 10MB</span>
      </div>
    </Upload>
  );

  // Popover 内容
  const popoverContent = (
    <div className={styles.popoverContent}>
      <Segmented
        className={styles.segmented}
        block
        options={[
          { label: '选择图标', value: 'select' },
          { label: '上传图片', value: 'upload' },
        ]}
        value={mode}
        onChange={(v) => setMode(v as 'select' | 'upload')}
      />
      {mode === 'select' ? renderSelectContent() : renderUploadContent()}
    </div>
  );

  // 渲染触发器内容
  const renderTriggerContent = () => {
    if (value) {
      return (
        <div className={styles.iconPreview}>
          {isUrl ? (
            <img src={value} alt="icon" />
          ) : isLobeIcon ? (
            renderLobeIcon(value, 48)
          ) : (
            <span>{value}</span>
          )}
          {!disabled && (
            <div className={styles.iconOverlay}>
              <span className={styles.deleteButton} onClick={handleDelete}>
                <DeleteOutlined />
              </span>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className={styles.triggerContent}>
        <PlusOutlined style={{ fontSize: 20 }} />
        <span>{placeholder}</span>
      </div>
    );
  };

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open && !disabled}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <div
        className={cx(styles.trigger, open && styles.triggerFocused)}
        style={{
          width,
          height,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {renderTriggerContent()}
      </div>
    </Popover>
  );
};

export default IconPicker;
